/**
 * The sidebar's New menu.
 *
 * Four things you can start, in the order you are likely to want them, with
 * the cross-product one below a rule. Dismissal is the contract both other
 * popovers in this app use: outside mousedown, and Escape.
 *
 * **The panel is positioned `fixed`, from the button's own rect.** The sidebar
 * is `overflow: hidden` — it has to be, so the nav scrolls without the
 * wordmark leaving — so an absolutely positioned panel is clipped to a 264px
 * column, and to 76px when the rail is collapsed.
 *
 * The folder and upload items need the folder you are standing in, which only
 * the file list knows. See newactions.ts: it registers them while mounted, and
 * when it is not, the menu stashes the intent and navigates there.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ds } from '../ds';
import { useRouter } from '../router';
import { useApp } from '../state';
import { entitlements } from '@shared/entitlements';
import { setPendingNewAction, useNewActions, type NewActionKind } from '../newactions';
import { newScopeProjectHref } from '../scope';
import { accountOrigin } from '../pages/Vault';
import { SHADOW } from '../theme';
import { TierDiamond, popoverRow } from './Shell';
import { useT } from '../i18n';

interface Item {
  key: NewActionKind;
  label: string;
  icon: string;
}

const ITEMS: Item[] = [
  { key: 'newFolder', label: 'New folder', icon: 'folder-plus' },
  { key: 'uploadFiles', label: 'Upload files', icon: 'upload' },
  { key: 'uploadFolder', label: 'Upload folder', icon: 'folder-up' }
];

export function NewMenu({ open }: { open: boolean }) {
  const t = useT();
  const { Button, Icon } = ds();
  const { navigate } = useRouter();
  const { me } = useApp();
  const actions = useNewActions();

  const [menuOpen, setMenuOpen] = useState(false);
  const [rect, setRect] = useState<{ top: number; left: number } | null>(null);
  const anchor = useRef<HTMLDivElement>(null);

  const canScope = entitlements(me?.plan).scopeProjects !== 0;

  useLayoutEffect(() => {
    if (!menuOpen) return;
    const el = anchor.current?.getBoundingClientRect();
    if (el) setRect({ top: el.bottom + 6, left: el.left });
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (anchor.current && !anchor.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onEscape = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    // A menu pinned to a rect has to close rather than float away.
    const onScrollOrResize = () => setMenuOpen(false);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEscape);
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEscape);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [menuOpen]);

  const run = (kind: NewActionKind) => {
    setMenuOpen(false);
    if (actions) {
      actions[kind]();
      return;
    }
    // Somewhere without a current folder — settings, trash, the audit log.
    setPendingNewAction(kind);
    navigate('/');
  };

  return (
    <div ref={anchor} style={{ position: 'relative', padding: open ? '16px 14px 10px' : '16px 10px 10px' }}>
      <Button
        variant="primary"
        size="md"
        fullWidth
        onClick={() => setMenuOpen((v) => !v)}
      >
        {open ? 'New' : '+'}
      </Button>

      {menuOpen && rect && (
        <div
          role="menu"
          aria-label={t('new-menu.new')}
          // `mvIn` was named here for a keyframe that was never written, so this
          // menu has appeared instantly since the day it was built. `.mv-pop`
          // in styles.css is the real one, shared with the popovers and the
          // dialogs so all three arrive the same way.
          className="mv-pop"
          style={{
            position: 'fixed',
            top: rect.top,
            left: rect.left,
            width: 232,
            background: 'var(--surface-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 16,
            boxShadow: SHADOW.popover,
            padding: 6,
            zIndex: 20
          }}
        >
          {ITEMS.map((item) => (
            <button
              key={item.key}
              role="menuitem"
              type="button"
              className="mv-row"
              onClick={() => run(item.key)}
              style={popoverRow}
            >
              <Icon name={item.icon} size={16} />
              {item.label}
            </button>
          ))}

          <div style={{ height: 1, background: 'var(--border-subtle)', margin: '6px 4px' }} />

          {canScope ? (
            <a
              role="menuitem"
              href={newScopeProjectHref()}
              className="mv-row"
              style={{ ...popoverRow, textDecoration: 'none' }}
              onClick={() => setMenuOpen(false)}
            >
              <Icon name="crosshair" size={16} />
              New Scope project
            </a>
          ) : (
            <a
              role="menuitem"
              href={`${accountOrigin()}/pricing`}
              className="mv-row"
              style={{ ...popoverRow, textDecoration: 'none', color: 'var(--text-muted)' }}
              onClick={() => setMenuOpen(false)}
            >
              <Icon name="crosshair" size={16} />
              New Scope project
              <TierDiamond title={t('new-menu.included-with-pro')} />
            </a>
          )}
        </div>
      )}
    </div>
  );
}
